import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/useI18n';
import type { AccessibleChatbotWithIdentity } from '../../types/chatbot';
import { formatTimestamp } from '../../utils/formatUtils';
import { resolveChatbotListPath } from './chatbotsPageHelpers';

type ChatbotListCardProps = {
  entry: AccessibleChatbotWithIdentity;
  knowledgeBaseName: string;
};

export function ChatbotListCard({
  entry,
  knowledgeBaseName,
}: ChatbotListCardProps) {
  const { locale, translate } = useI18n();
  const navigate = useNavigate();
  const { chatbot, role } = entry;
  const trimmedDisplayName = chatbot.displayName?.trim() ?? '';
  const hasDisplayName = trimmedDisplayName !== '';
  const displayNameLabel = hasDisplayName
    ? trimmedDisplayName
    : translate('chatbots.noDisplayName');
  const isPublic = chatbot.isPublic === true;
  const updatedAt = chatbot.stamp
    ? formatTimestamp(chatbot.stamp, locale)
    : translate('chatbots.notAvailable');

  return (
    <li>
      <button
        type="button"
        className="chatbots-page__card"
        onClick={() => navigate(resolveChatbotListPath(chatbot.id))}
      >
        <div className="chatbots-page__card-heading">
          <h2 className="chatbots-page__card-title">{chatbot.name}</h2>
          <div className="chatbots-page__card-badges">
            <span
              className={[
                'chatbots-page__card-badge',
                isPublic && 'chatbots-page__card-badge--public',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {isPublic
                ? translate('chatbots.visibility.public')
                : translate('chatbots.visibility.private')}
            </span>
            <span className="chatbots-page__card-badge chatbots-page__card-badge--role">
              {translate(`chatbots.members.roles.${role}`)}
            </span>
          </div>
        </div>
        <p
          className={[
            'chatbots-page__card-subtitle',
            !hasDisplayName && 'chatbots-page__card-subtitle--empty',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {displayNameLabel}
        </p>
        <dl className="chatbots-page__card-stats">
          <div className="chatbots-page__card-stat">
            <dt>{translate('chatbots.stats.knowledgeBase')}</dt>
            <dd>{knowledgeBaseName}</dd>
          </div>
          <div className="chatbots-page__card-stat">
            <dt>{translate('chatbots.stats.lastModified')}</dt>
            <dd>{updatedAt}</dd>
          </div>
        </dl>
      </button>
    </li>
  );
}
